import { SkyButton, SkyCard, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { ConnectorAccessView } from "@shared/types/connectorAccess";
import type { ConnectorSummary } from "@shared/types/connectors";
import { LoadingRows, PermissionNotice } from "@/components/access/ScreenStates";
import { ErrorState } from "@/components/platform/ScreenState";
import {
  ConnectorChoiceFields,
  OffersNoneDialog,
  draftFromView,
  offersNone,
  sameChoice,
  type ConnectorChoiceDraft,
} from "@/components/settings/ConnectorChoiceFields";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useConnectorAccess, useSaveConnectorAccess } from "@/hooks/use-connector-access";
import { useConnectors } from "@/hooks/use-connectors";
import { usePermissions } from "@/hooks/use-permissions";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";

/**
 * Settings > Connectors (T-51, round 8; a customer asked to see 4 of 20): which connectors the
 * tenant offers for NEW connections, among those the platform makes available. "All available
 * connectors" is the default and includes any added later; "Only selected connectors" offers just
 * the ticked ones, and none at all after a warning. Existing connections keep working whatever is
 * chosen. While Skypoint manages the choice ("Locked by Skypoint", set from Tenants) the tab reads
 * it only and says so.
 *
 * A tenant setting inside the instance's Settings: it asks for `tenant.instances.manage`
 * (TenantAdmin and above), the key `GET|PUT /tenants/:id/connector-access` asks for.
 */

function ConnectorChoice({
  tenantId,
  tenantName,
  view,
  catalog,
}: {
  tenantId: string;
  tenantName: string;
  view: ConnectorAccessView;
  catalog: ConnectorSummary[];
}) {
  const save = useSaveConnectorAccess(tenantId);
  const notice = useNotice();
  const saveError = useInlineError();
  const [draft, setDraft] = useState<ConnectorChoiceDraft>(() => draftFromView(view));
  const [confirmingNone, setConfirmingNone] = useState(false);

  const locked = view.managedBy === "platform";
  // The TenantAdmin chooses among the connectors the platform makes available.
  // Hidden ones are not in the catalog the tenant reads; the view names them so they can be shown
  // again.
  const shown = catalog
    .filter((connector) => connector.offer !== "not_available")
    .map((connector) => ({ name: connector.name, displayName: connector.displayName }));
  const options = [
    ...shown,
    ...view.hiddenConnectors.filter((hidden) => !shown.some((entry) => entry.name === hidden.name)),
  ].sort((a, b) => a.displayName.localeCompare(b.displayName));
  const changed = !sameChoice(draft, draftFromView(view));

  function submit() {
    saveError.clear();
    save.mutate(
      { mode: draft.mode, connectors: draft.connectors, hidden: draft.hidden },
      {
        onSuccess: (saved) => {
          setDraft(draftFromView(saved));
          notice.success(
            saved.mode === "all"
              ? "Saved. New connections offer every available connector that is not hidden."
              : `Saved. New connections offer ${saved.effective.length} of ${options.length} connectors.`,
          );
        },
        onError: (error) => saveError.show(error, "The connector choice was not saved."),
      },
    );
  }

  return (
    <SkyCard className="flex flex-col gap-4 p-4" testId="connector-access">
      <div>
        <SkyText as="h2" size={400} weight="semibold" block>
          Connectors offered for new connections
        </SkyText>
        <SkyText as="p" size={300} color="muted" block className="mt-1">
          {`Choose which connectors people in ${tenantName} can pick when they create a connection. This applies to every instance of ${tenantName}. Existing connections keep working whatever you choose.`}
        </SkyText>
      </div>

      {locked ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="connector-access-locked"
          title="Locked by Skypoint"
          message="Skypoint manages the connectors offered to your organisation. Contact Skypoint to change them."
        />
      ) : null}

      <ConnectorChoiceFields
        draft={draft}
        options={options}
        onChange={setDraft}
        readOnly={locked}
        tenantName={tenantName}
        testIdPrefix="connector-access"
      />

      <InlineErrorBar
        error={saveError.error}
        title="The connector choice was not saved"
        onDismiss={saveError.clear}
        testId="connector-access-error"
      />

      {locked ? null : (
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <SkyText as="p" size={200} color="muted">
            Only new connections are affected.
          </SkyText>
          <span className="ml-auto flex items-center gap-2">
            <SkyButton
              appearance="subtle"
              testId="connector-access-discard"
              disabled={!changed || save.isPending}
              onClick={() => setDraft(draftFromView(view))}
            >
              Discard
            </SkyButton>
            <SkyButton
              appearance="primary"
              testId="connector-access-save"
              disabled={!changed}
              loading={save.isPending}
              onClick={() => (offersNone(draft, options) ? setConfirmingNone(true) : submit())}
            >
              Save changes
            </SkyButton>
          </span>
        </div>
      )}

      <OffersNoneDialog
        open={confirmingNone}
        onOpenChange={setConfirmingNone}
        onConfirm={() => {
          setConfirmingNone(false);
          submit();
        }}
        tenantName={tenantName}
        testId="connector-access-none-dialog"
      />
    </SkyCard>
  );
}

export default function ConnectorAccessTab() {
  const { can, isResolved } = usePermissions();
  const { selectedInstance } = useTenantInstance();
  const allowed = isResolved && can("tenant.instances.manage");
  const tenantId = selectedInstance?.tenantId ?? null;
  const view = useConnectorAccess(tenantId, allowed);
  const catalog = useConnectors(allowed);

  function body() {
    if (!isResolved) return <LoadingRows rows={4} columns={2} />;
    if (!allowed) return <PermissionNotice permission="tenant.instances.manage" />;
    if (view.isError || catalog.isError) {
      return (
        <ErrorState
          title="The connector choice did not load"
          error={view.error ?? catalog.error}
          onRetry={() => {
            void view.refetch();
            void catalog.refetch();
          }}
        />
      );
    }
    if (!view.data || !catalog.data || !tenantId || !selectedInstance) {
      return <LoadingRows rows={4} columns={2} />;
    }
    return (
      <ConnectorChoice
        // Another tenant, or a choice Skypoint changed, starts from what is stored.
        key={`${tenantId}-${view.data.updatedAt ?? "never"}-${view.data.managedBy}`}
        tenantId={tenantId}
        tenantName={selectedInstance.tenantDisplayName}
        view={view.data}
        catalog={catalog.data}
      />
    );
  }

  return (
    <div className="space-y-4">
      <SettingsTabs active="connectors" />
      {body()}
    </div>
  );
}
